import { useState, useEffect } from "react";
import { usePersistedState } from "../../hooks/usePersistedState";
import PropTypes from "prop-types";
import {
  Box,
  Paper,
  Stack,
  Grid,
  IconButton,
  Table,
  TableContainer,
  TableRow,
  TableCell,
  TableBody,
  TableHead,
  TablePagination,
  TableFooter,
  Tooltip,
  Snackbar,
  Alert,
  Typography,
  CircularProgress,
  Modal,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  MenuItem,
  Checkbox,
  ListItemText,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import TableSortLabel from "@mui/material/TableSortLabel";
import { useNavigate } from "react-router-dom";
import { Edit, Visibility, Delete, ContentCopy } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import {
  LastPage,
  FirstPage,
  KeyboardArrowRight,
  KeyboardArrowLeft,
  Close,
  AddLink,
  Link,
} from "@mui/icons-material";
import API from "../../config/axios";
import ProfilePreviewDialog from "./profilePreviewDialog";
import { getUserData } from "../../utils/utilities.js";
import LinkProfileModal from "./PageModals/linkProfileModal";
import ManageLinkedProfileModal from "./PageModals/manageLinkedProfileModal";
import SearchBar from "../../components/SearchBar.js";
import Button from "../../components/CommonButton.js";
import DeleteConfirmationDialog from "../../components/DeleteConfirmationDialog.js";

function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? <LastPage /> : <FirstPage />}
      </IconButton>
      <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
        {theme.direction === "rtl" ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPage /> : <LastPage />}
      </IconButton>
    </Box>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

const List = () => {
  const role = getUserData().role;
  const navigate = useNavigate();
  const [totalRows, setTotalRows] = useState(5);
  const [rowsPerPage, setRowsPerPage] = usePersistedState("employee_rowsPerPage", 10);
  const [page, setPage] = usePersistedState("employee_page", 0);
  const [order, setOrder] = usePersistedState("employee_order", "asc");
  const [orderBy, setOrderBy] = usePersistedState("employee_orderBy", "name");
  const [list, setList] = useState([]);
  const [search, setSearch] = usePersistedState("employee_search", null);
  const [positionsList, setPositionsList] = useState([]);
  const [selectedPositions, setSelectedPositions] = usePersistedState("employee_selectedPositions", []);
  const [selectedExperiences, setSelectedExperiences] = usePersistedState("employee_selectedExperiences", []);
  const [companiesList, setCompaniesList] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = usePersistedState("employee_selectedCompanies", []);
  const [displayAlert, setDisplayAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertVariant, setAlertVariant] = useState("success");
  const [copyingProfile, setCopyingProfileLoader] = useState({});
  const [deleteConfirmationDialog, setDeleteConfirmationDialog] = useState(false);
  const [deleteProfile, setDeleteProfile] = useState({});
  const [deleteAlert, setDeleteAlert] = useState(false);
  const [generatingPreview, setGeneratingPreview] = useState(false);
  const [previewSourcePath, setPreviewSourcePath] = useState("");
  const [showProfilePreviewDialog, setProfilePreviewDialog] = useState(false);
  const [previewLoader, setPreviewLoader] = useState(false);
  const [linkProfileModal, setLinkProfileModal] = useState(false);
  const [companyId, setCompanyId] = useState("");
  const [linkProfileId, setLinkProfileId] = useState("");
  const [manageLinkedProfileModal, setManageLinkedProfileModal] = useState(false);
  const [profile, setProfile] = useState({});


  const getExpertises = () => {
    API.get("admin/expertises", { params: { rowsPerPage: 0 } })
      .then((res) => {
        if (res.status === 200) {
          setPositionsList(res.data.data?.results || res.data.data?.list || []);
        }
      })
      .catch((err) => console.log(err));
  };

  useEffect(() => {
    getExpertises();
  }, []);

  const handleDeleteConfirmationClose = () => {
    setDeleteConfirmationDialog(false);
  };

  // displays alert message
  const displayAlertMessage = (variant, message) => {
    setDisplayAlert(true);
    setAlertMessage(message);
    setAlertVariant(variant);
  };

  // close alert message
  const closeAlertMessage = () => {
    setDisplayAlert(false);
    setAlertMessage("");
  };

  const resolveList = (searchValue = search) => {
    if (role === "SuperAdmin") {
      getEmployeeProfileList(searchValue, rowsPerPage, page);
    } else if (role === "CompanyAdmin" || role === "Company") {
      getProfilesAssignedToCompany(searchValue, rowsPerPage, page);
    } else if (role == "Admin") {
      getProfilesAssignedToAdmin(searchValue, rowsPerPage, page);
    } else {
      getProfilesAssignedToUser();
    }
  };

  // make copy of the selected profile
  const makeProfileCopy = (profile_id) => {
    setCopyingProfileLoader({ ...copyingProfile, [profile_id]: true });

    API.post(`admin/employee_profile/${profile_id}/make_copy`)
      .then((res) => {
        const data = res.data;

        if (data.status) {
          displayAlertMessage("success", "Profile Copied Successfully");
          resolveList();
        } else {
          displayAlertMessage("error", "Something went wrong");
        }
      })
      .finally(() => {
        setCopyingProfileLoader({ ...copyingProfile, [profile_id]: false });
      });
  };

  const resolvePaginationChange = (search, rowsPerPage, page) => {
    if (role === "SuperAdmin") {
      getEmployeeProfileList(search, rowsPerPage, page);
    } else if (role === "CompanyAdmin" || role === "Company") {
      getProfilesAssignedToCompany(search, rowsPerPage, page);
    } else if (role == "Admin") {
      getProfilesAssignedToAdmin(search, rowsPerPage, page);
    } else {
      getProfilesAssignedToUser(search, rowsPerPage, page);
    }
  };

  const handleChangePage = (e, newPage) => {
    setPage(newPage);
    resolvePaginationChange(search, rowsPerPage, newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
    resolvePaginationChange(search, e.target.value, page);
  };

  // ** API Services ** //
  const getEmployeeProfileList = (
    searchValue = search,
    recordsPerPage = rowsPerPage,
    currentPage = page,
  ) => {
    const query = {
      pagination: recordsPerPage === -1 ? false : true,
      search: searchValue,
      rowsPerPage: recordsPerPage,
      page: currentPage + 1,
      sortBy: orderBy,
      descending: order === "asc" ? false : true,
      positions: selectedPositions.length > 0 ? selectedPositions : undefined,
      experiences: selectedExperiences.length > 0 ? selectedExperiences : undefined,
    };
    API.get("/employee-profile/get-employee-list", {
      params: { ...query },
    })
      .then((res) => {
        if (res.status === 200) {
          const data = res.data.data;

          setList(
            data?.list?.map((rec) => ({
              ...rec,
              languages: rec.languages?.map((lang) => ({
                name: lang.name?.name || lang.name,
                value: lang.value,
              })),
            })),
          );

          setRowsPerPage(data?.rowsPerPage);
          setTotalRows(data?.total);
        } 
      })
      .catch((err) => console.log(err));
  };

  const getProfilesAssignedToCompany = (search, rowsPerPage, page) => {
    const query = {
      search,
      rowsPerPage,
      page: page + 1,
      pagination: rowsPerPage === -1 ? false : true,
      sortBy: orderBy,
      descending: order === "asc" ? false : true,
      positions: selectedPositions.length > 0 ? selectedPositions : undefined,
      experiences: selectedExperiences.length > 0 ? selectedExperiences : undefined,
    };
    const body = {
      companyId: getUserData().company_id,
    };
    API.post("/employee-profile/get-profile-assigned-to-company", body, {
      params: { ...query },
    })
      .then((res) => {
        if (res.status === 200) {
          setList(
            res.data.data?.list?.map((rec) => ({
              ...rec,
              languages: rec.languages?.map((lang) => ({
                name: lang.name?.name || lang.name,
                value: lang.value,
              })),
            })),
          );
          setTotalRows(res.data.data?.total);
        }
      })
      .catch((err) => console.log(err));
  };

  const getProfilesAssignedToAdmin = (search, rowsPerPage, page) => {
    const query = {
      search,
      rowsPerPage,
      page: page + 1,
      pagination: rowsPerPage === -1 ? false : true,
      sortBy: orderBy,
      descending: order === "asc" ? false : true,
      positions: selectedPositions.length > 0 ? selectedPositions : undefined,
      experiences: selectedExperiences.length > 0 ? selectedExperiences : undefined,
    };
    const body = {
      adminId: getUserData().id,
    };
    API.post("/employee-profile/get-profile-assigned-to-admin", body, {
      params: { ...query },
    })
      .then((res) => {
        if (res.status === 200) {
          setList(
            res.data.data?.list?.map((rec) => ({
              ...rec,
              languages: rec.languages?.map((lang) => ({
                name: lang.name?.name || lang.name,
                value: lang.value,
              })),
            })),
          );
          setTotalRows(res.data.data?.total);
        }
      })
      .catch((err) => console.log(err));
  };

  const getProfilesAssignedToUser = () => {
    const query = {
      search,
      rowsPerPage,
      page: page + 1,
      pagination: rowsPerPage === -1 ? false : true,
      sortBy: orderBy,
      descending: order === "asc" ? false : true,
      positions: selectedPositions.length > 0 ? selectedPositions : undefined,
      experiences: selectedExperiences.length > 0 ? selectedExperiences : undefined,
    };
    const body = {
      userId: getUserData().id,
    };
    API.post("/employee-profile/get-profile-assigned-to-user", body, {
      params: { ...query },
    })
      .then((res) => {
        if (res.status === 200) {
          setList(
            res.data.data?.list?.map((rec) => ({
              ...rec,
              languages: rec.languages?.map((lang) => ({
                name: lang.name?.name || lang.name,
                value: lang.value,
              })),
            })),
          );
          setTotalRows(res.data.data?.total);
        }
      })
      .catch((err) => console.log(err));
  };

  const generatePreview = async (employeeData) => {
    setGeneratingPreview(true);
    setPreviewLoader(true);
    console.log("employeeData", employeeData);
    const validSkills = new Set();
    if (employeeData.expertise && Array.isArray(employeeData.expertise.skills)) {
      employeeData.expertise.skills.forEach(s => validSkills.add(s));
    } else {
      const expId = employeeData.expertise?._id || employeeData.expertise;
      const opt = positionsList.find(p => p._id === expId || p.id === expId);
      if (opt && Array.isArray(opt.skills)) {
        opt.skills.forEach(s => validSkills.add(s));
      }
    }
    if (Array.isArray(employeeData.experienceDetails)) {
      employeeData.experienceDetails.forEach(exp => {
        if (exp.tech) {
          const opt = positionsList.find(p => p.name === exp.tech);
          if (opt && Array.isArray(opt.skills)) {
            opt.skills.forEach(s => validSkills.add(s));
          }
        }
      });
    }

    const body = {
      ...employeeData,
      company: employeeData.company || {},
      name: employeeData.name || "",
      timeFrom: employeeData.timeFrom,
      timeTill: employeeData.timeTill,
      skills: (employeeData.skills || []).filter(s => validSkills.size === 0 || validSkills.has(s)),
      graduationDate: employeeData.graduationDate,
      religion: employeeData.religion?._id,
      nationality: employeeData.nationality?._id,
      expertise: [employeeData.expertise?._id],
      languages: employeeData.languages.map((lang) => ({
        value: lang.value,
        name: lang.name,
      })),
    };
    console.log("body", body);

    const result = await getEmployeeProfilePic(body._id);

    body.profilePic = result.data.data?.profile_pic || employeeData.profile_pic || "";

    delete body._id;
    delete body.profile_pic;
    delete body.deleted;
    delete body.createdAt;
    delete body.updatedAt;
    delete body.__v;
    delete body.createdBy;

    API.post("admin/employee_profile/generate_preview", body, {
      responseType: "arraybuffer",
    })
      .then((res) => {
        if (res.headers["content-type"] == "application/pdf") {
          // display pdf preview
          let blob = new Blob([res.data], {
            type: res.headers["content-type"],
          });
          setPreviewSourcePath(URL.createObjectURL(blob));
          setProfilePreviewDialog(true);
        } else {
          // handle error
          try {
            let response = JSON.parse(String.fromCharCode.apply(null, new Uint8Array(res.data)));

            if (!response.status) {
              displayAlertMessage("error", "Something went wrong");
            }
          } catch (error) {
            displayAlertMessage("error", "Something went wrong");
          }
        }
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setPreviewLoader(false);
        setGeneratingPreview(false);
      });
  };

  // delete record
  const handleDelete = () => {
    if (deleteProfile) {
      API.delete("/employee-profile/delete", {
        params: { id: deleteProfile._id },
      }).then((res) => {
        if (res.status === 200 && res.data.status) {
          setDeleteAlert(true);
          resolveList();
        }
      });
    }
  };

  const createSortHandler = (property) => (event) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const headCells = [
    {
      id: "name",
      numeric: false,
      disablePadding: true,
      label: "Employee Name",
      sortable: true,
    },
    {
      id: "company",
      numeric: false,
      disablePadding: true,
      label: "Company Name",
      sortable: true,
    },
    {
      id: "createdBy",
      numeric: false,
      disablePadding: true,
      label: "Created By",
      sortable: true,
    },
    {
      id: "action",
      numeric: false,
      disablePadding: true,
      label: "Actions",
      sortable: false,
    },
  ];

  const getEmployeeProfilePic = async (id) => {
    const params = { id };
    return await API.get("/employee-profile/get-employee-profile-pic", {
      params,
    });
  };

  useEffect(() => {
    resolveList();
  }, [page, rowsPerPage, order, orderBy, selectedPositions, selectedExperiences, selectedCompanies]);

  return (
    <div>
      <Grid container rowSpacing={0}>
        <Grid item xs={12} sx={{ pb: 2 }}>
          <Typography variant="body1" sx={{ fontSize: "20px" }} color="#474747">
            Employee Profile Management
          </Typography>
        </Grid>
        <Grid
          item
          xs={12}
          px={"25px"}
          sx={{ borderRadius: "10px", backgroundColor: "white", py: 3 }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems="stretch"
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Box sx={{ display: "flex", alignItems: "stretch", gap: 2 }}>
              <SearchBar value={search || ""}
                sx={{ width: "100%" }}
                onSearch={(search) => {
                  setSearch(search);
                  setPage(0);
                  resolvePaginationChange(search, rowsPerPage, 0);
                }}
              />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                            <FormControl sx={{ minWidth: "150px", backgroundColor: "white" }} size="small">
                <InputLabel id="position-label">Position</InputLabel>
                <Select
                  labelId="position-label"
                  multiple
                  value={selectedPositions}
                  onChange={(e) => { setSelectedPositions(e.target.value); setPage(0); }}
                  input={<OutlinedInput label="Position" />}
                  MenuProps={{ PaperProps: { style: { maxHeight: 250 } } }}
                  renderValue={(selected) => {
                    if (selected.length === 0) return "";
                    if (selected.length === 1) return positionsList.find(p => p._id === selected[0])?.name || selected[0];
                    return `${selected.length} Selected`;
                  }}
                  sx={{ "& .MuiSelect-select": { paddingRight: selectedPositions.length > 0 ? "55px !important" : undefined } }}
                  endAdornment={
                    selectedPositions.length > 0 && (
                      <IconButton
                        size="small"
                        sx={{ position: "absolute", right: 28 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setSelectedPositions([]);
                          setPage(0);
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    )
                  }
                >
                  {positionsList.map((pos) => (
                    <MenuItem key={pos._id} value={pos._id}>
                      <Checkbox checked={selectedPositions.indexOf(pos._id) > -1} />
                      <ListItemText primary={pos.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: "150px", backgroundColor: "white" }} size="small">
                <InputLabel id="experience-label">Experience</InputLabel>
                <Select
                  labelId="experience-label"
                  multiple
                  value={selectedExperiences}
                  onChange={(e) => { setSelectedExperiences(e.target.value); setPage(0); }}
                  input={<OutlinedInput label="Experience" />}
                  MenuProps={{ PaperProps: { style: { maxHeight: 250 } } }}
                  renderValue={(selected) => {
                    if (selected.length === 0) return "";
                    if (selected.length === 1) return selected[0];
                    return `${selected.length} Selected`;
                  }}
                  sx={{ "& .MuiSelect-select": { paddingRight: selectedExperiences.length > 0 ? "55px !important" : undefined } }}
                  endAdornment={
                    selectedExperiences.length > 0 && (
                      <IconButton
                        size="small"
                        sx={{ position: "absolute", right: 28 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setSelectedExperiences([]);
                          setPage(0);
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    )
                  }
                >
                  {["Below 5 years", "5-10 years", "10-15 years", "15+ years"].map((exp) => (
                    <MenuItem key={exp} value={exp}>
                      <Checkbox checked={selectedExperiences.indexOf(exp) > -1} />
                      <ListItemText primary={exp} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button onClick={() => navigate("/employee-profiles/create")}>
                Create New Profile
              </Button>
            </Box>
          </Stack>

          <TableContainer component={Paper} elevation={0}>
            <Table aria-label="simple table" className={"custom-table br-0"}>
              <TableHead sx={{ backgroundColor: "#f4f6f9" }}>
                <TableRow>
                  <TableCell
                    sx={{
                      width: "80px",
                      minWidth: "80px",
                      textAlign: "center",
                    }}
                  >
                    No.
                  </TableCell>
                  {headCells.map((headCell) => (
                    <TableCell
                      size="small"
                      key={headCell.id}
                      align={headCell.numeric ? "right" : "left"}
                      sortDirection={orderBy === headCell.id ? order : false}
                    >
                      {headCell.sortable ? (
                        <TableSortLabel
                          active={orderBy === headCell.id}
                          direction={orderBy === headCell.id ? order : "asc"}
                          onClick={createSortHandler(headCell.id)}
                        >
                          {headCell.label}
                          {orderBy === headCell.id ? (
                            <Box component="span" sx={visuallyHidden}>
                              {order === "desc" ? "sorted descending" : "sorted ascending"}
                            </Box>
                          ) : null}
                        </TableSortLabel>
                      ) : (
                        headCell.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              {!list?.length ? (
                <TableBody>
                  <TableRow>
                    <TableCell size="small" colSpan={5}>
                      <Typography sx={{ width: "100%", textAlign: "center" }}>
                        No Data Available
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              ) : (
                <TableBody>
                  {list.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell style={{ textAlign: "center" }}>
                        {page * rowsPerPage + (index + 1)}
                      </TableCell>
                      <TableCell size="small" style={{ width: "25%" }}>
                        {row.name}
                      </TableCell>
                      <TableCell size="small">{row.company?.name || ""}</TableCell>
                      <TableCell size="small" sx={{ width: "180px", minWidth: "80px" }}>
                        {row?.createdBy?.name}
                      </TableCell>
                      <TableCell sx={{ width: "80px", minWidth: "80px" }}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Tooltip title="Manage Linked Users">
                            <IconButton
                              color="inherit"
                              sx={{ padding: 0, marginLeft: "5px" }}
                              onClick={() => {
                                if (!row.assignedToUsers?.length && !row.assignedToAdmins?.length) {
                                  setAlertVariant("error");
                                  setAlertMessage("No user or admin linked to the profile!");
                                  setDisplayAlert(true);
                                  return;
                                }
                                setManageLinkedProfileModal(true);
                                setProfile(row);
                              }}
                            >
                              <Link fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Link Profile">
                            <IconButton
                              color="inherit"
                              sx={{ padding: 0, marginLeft: "5px" }}
                              onClick={() => {
                                setLinkProfileModal(true);
                                setCompanyId(row.company?._id || "");
                                setLinkProfileId(row._id || "");
                              }}
                            >
                              <AddLink fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {!copyingProfile[row._id] ? (
                            <Tooltip title="Copy Record">
                              <IconButton
                                color="inherit"
                                sx={{ padding: 0, marginLeft: "5px" }}
                                onClick={() => {
                                  makeProfileCopy(row._id);
                                }}
                              >
                                <ContentCopy fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <CircularProgress
                              color="primary"
                              size="20px"
                              sx={{ marginLeft: "4px", marginRight: "5px" }}
                            />
                          )}
                          <Tooltip title="Preview">
                            <IconButton
                              color="inherit"
                              sx={{ padding: 0, marginLeft: "5px" }}
                              onClick={() => generatePreview(row)}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              color="inherit"
                              sx={{ padding: 0, marginLeft: "5px" }}
                              onClick={() => {
                                navigate(`/employee-profiles/edit/${row._id}`);
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              color="inherit"
                              sx={{ padding: 0, marginLeft: "5px" }}
                              onClick={() => {
                                setDeleteProfile(row);
                                setDeleteConfirmationDialog(true);
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
                    count={totalRows}
                    colSpan={5}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    ActionsComponent={TablePaginationActions}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={displayAlert}
        onClose={closeAlertMessage}
        message={displayAlert}
        key={"system-message"}
        autoHideDuration={2000}
      >
        <Alert severity={alertVariant}>
          {alertMessage}
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            sx={{ ml: 2 }}
            onClick={closeAlertMessage}
          >
            <Close fontSize="small" />
          </IconButton>
        </Alert>
      </Snackbar>

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={deleteAlert}
        onClose={() => {
          setDeleteAlert(false);
          resolveList();
        }}
        key={"system-message"}
        autoHideDuration={2000}
      >
        <Alert severity={"success"}>Profile Deleted Successfully</Alert>
      </Snackbar>

      {/* Delete Profile Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteConfirmationDialog}
        onDelete={() => {
          handleDeleteConfirmationClose();
          handleDelete();
        }}
        onClose={handleDeleteConfirmationClose}
        name={`${deleteProfile.name}'s profile`}
      />

      {/* Profile Preview Dialog */}
      <ProfilePreviewDialog
        open={showProfilePreviewDialog}
        setOpen={setProfilePreviewDialog}
        sourcePath={previewSourcePath}
      />

      <Modal open={previewLoader}>
        <Box
          sx={{
            display: "flex",
            height: "100%",
            width: "100%",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "20px 30px",
              borderRadius: "10px",
              textAlign: "center",
            }}
          >
            <CircularProgress />
          </div>
        </Box>
      </Modal>

      {linkProfileModal ? (
        <LinkProfileModal
          open={linkProfileModal}
          setOpen={setLinkProfileModal}
          profileId={linkProfileId}
          setProfileId={setLinkProfileId}
          companyId={companyId}
          setAlert={setDisplayAlert}
          setAlertMessage={setAlertMessage}
          setAlertVariant={setAlertVariant}
          getList={resolveList}
        />
      ) : null}
      {manageLinkedProfileModal ? (
        <ManageLinkedProfileModal
          open={manageLinkedProfileModal}
          setOpen={setManageLinkedProfileModal}
          setAlert={setDisplayAlert}
          setAlertMessage={setAlertMessage}
          setAlertVariant={setAlertVariant}
          data={profile}
          getList={resolveList}
        />
      ) : null}
    </div>
  );
};

export default List;
